import {
  ArrowUpRight,
  Check,
  CloudUpload,
  Database,
  FileSpreadsheet,
  FolderOpen,
  HardDrive,
  Link2,
  LoaderCircle,
  Mail,
  Map,
  MapPinned,
  RefreshCw,
  Save,
  ShieldCheck,
  UserRoundCog,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { requireFirebaseFunctions } from '../lib/firebase';
import { ProvinceQueuesPage } from './ProvinceQueuesPage';
import { TerritorialAreasPage } from './TerritorialAreasPage';
import { UsersPage } from './UsersPage';

type ReportMailboxStatus = {
  connected: boolean;
  mailboxEmail: string;
  recipientEmail: string;
  watch: {
    active: boolean;
    expirationAt: string | null;
    lastProcessedAt: string | null;
  };
};

type OAuthStartResponse = {
  authorizationUrl: string;
  expiresAt: string;
  mailboxEmail: string;
  recipientEmail: string;
};

type LoadingAction = 'connect' | 'watch' | 'refresh' | null;

type DriveSourceForm = {
  workspaceFolderUrl: string;
  mailImportFolderUrl: string;
  responsesSpreadsheetUrl: string;
};

type DriveConnectionCheck = {
  connected: boolean;
  name: string | null;
  canWrite: boolean;
};

type DriveSourceSettings = DriveSourceForm & {
  updatedAt: string | null;
  updatedBy: string | null;
  connections: {
    workspace: DriveConnectionCheck;
    mailImport: DriveConnectionCheck;
    responses: DriveConnectionCheck;
  };
};

const demoSourceSettings: DriveSourceSettings = {
  workspaceFolderUrl: 'https://drive.google.com/drive/folders/1NnIUfXP-dQlPplrtDN7lG4Om6Is7wk3A',
  mailImportFolderUrl: 'https://drive.google.com/drive/folders/1CiKeIAPzbkunDYO9EN0jMGIt_A8TjvIm',
  responsesSpreadsheetUrl: 'https://docs.google.com/spreadsheets/d/1L3oaoFKiuZMXdUwPvpBjMZ1GT8vjbhE2YFM9AuFzZQE/edit',
  updatedAt: null,
  updatedBy: null,
  connections: {
    workspace: { connected: true, name: 'Talos', canWrite: true },
    mailImport: { connected: true, name: 'Mail', canWrite: true },
    responses: { connected: true, name: 'Risposte', canWrite: true },
  },
};

function formattedDate(value: string | null): string {
  if (!value) return 'Non ancora disponibile';
  return new Intl.DateTimeFormat('it-IT', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

async function fetchReportMailboxStatus(demo: boolean): Promise<ReportMailboxStatus> {
  if (demo) {
    return {
      connected: false,
      mailboxEmail: 'francesco.pace@justbit.it',
      recipientEmail: 'reportcrm-of@justbit.it',
      watch: { active: false, expirationAt: null, lastProcessedAt: null },
    };
  }
  const callable = httpsCallable<void, ReportMailboxStatus>(
    requireFirebaseFunctions(),
    'getReportMailboxStatus',
  );
  return (await callable()).data;
}

async function fetchDriveSourceSettings(demo: boolean): Promise<DriveSourceSettings> {
  if (demo) return demoSourceSettings;
  const callable = httpsCallable<void, DriveSourceSettings>(
    requireFirebaseFunctions(),
    'getDriveSourceSettings',
  );
  return (await callable()).data;
}

function sourceForm(settings: DriveSourceSettings): DriveSourceForm {
  return {
    workspaceFolderUrl: settings.workspaceFolderUrl,
    mailImportFolderUrl: settings.mailImportFolderUrl,
    responsesSpreadsheetUrl: settings.responsesSpreadsheetUrl,
  };
}

function readableError(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) return fallback;
  return error.message.replace(/^Firebase:\s*/i, '').replace(/^Functions:\s*/i, '') || fallback;
}

function WorkspaceSettingsPanel() {
  const { user } = useAuth();
  const canEditSources = Boolean(user);
  const [sources, setSources] = useState<DriveSourceSettings | null>(null);
  const [sourceDraft, setSourceDraft] = useState<DriveSourceForm>({
    workspaceFolderUrl: '',
    mailImportFolderUrl: '',
    responsesSpreadsheetUrl: '',
  });
  const [sourceLoading, setSourceLoading] = useState(true);
  const [sourceSaving, setSourceSaving] = useState(false);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [sourceSuccess, setSourceSuccess] = useState<string | null>(null);
  const [status, setStatus] = useState<ReportMailboxStatus | null>(null);
  const [action, setAction] = useState<LoadingAction>('refresh');
  const [error, setError] = useState<string | null>(null);

  const loadSources = async () => {
    setSourceLoading(true);
    setSourceError(null);
    try {
      const result = await fetchDriveSourceSettings(Boolean(user?.demo));
      setSources(result);
      setSourceDraft(sourceForm(result));
    } catch (loadError) {
      setSourceError(readableError(loadError, 'Non è stato possibile leggere i collegamenti Drive.'));
    } finally {
      setSourceLoading(false);
    }
  };

  const loadStatus = async () => {
    setAction('refresh');
    setError(null);
    try {
      setStatus(await fetchReportMailboxStatus(Boolean(user?.demo)));
    } catch {
      setError('Non è stato possibile leggere lo stato della casella Gmail.');
    } finally {
      setAction(null);
    }
  };

  useEffect(() => {
    let active = true;
    void fetchReportMailboxStatus(Boolean(user?.demo))
      .then((result) => {
        if (active) setStatus(result);
      })
      .catch(() => {
        if (active) setError('Non è stato possibile leggere lo stato della casella Gmail.');
      })
      .finally(() => {
        if (active) setAction(null);
      });
    return () => { active = false; };
  }, [user?.demo]);

  useEffect(() => {
    let active = true;
    void fetchDriveSourceSettings(Boolean(user?.demo))
      .then((result) => {
        if (!active) return;
        setSources(result);
        setSourceDraft(sourceForm(result));
      })
      .catch((loadError: unknown) => {
        if (active) setSourceError(readableError(loadError, 'Non è stato possibile leggere i collegamenti Drive.'));
      })
      .finally(() => {
        if (active) setSourceLoading(false);
      });
    return () => { active = false; };
  }, [user?.demo]);

  const updateSource = (field: keyof DriveSourceForm, value: string) => {
    setSourceDraft((current) => ({ ...current, [field]: value }));
    setSourceError(null);
    setSourceSuccess(null);
  };

  const saveSources = async () => {
    setSourceSaving(true);
    setSourceError(null);
    setSourceSuccess(null);
    try {
      const callable = httpsCallable<DriveSourceForm, DriveSourceSettings>(
        requireFirebaseFunctions(),
        'saveDriveSourceSettings',
      );
      const result = (await callable(sourceDraft)).data;
      setSources(result);
      setSourceDraft(sourceForm(result));
      setSourceSuccess('Collegamenti verificati e salvati. Le elaborazioni useranno subito queste sorgenti.');
    } catch (saveError) {
      setSourceError(readableError(saveError, 'Non è stato possibile verificare e salvare i collegamenti.'));
    } finally {
      setSourceSaving(false);
    }
  };

  const connectMailbox = async () => {
    setAction('connect');
    setError(null);
    try {
      const callable = httpsCallable<void, OAuthStartResponse>(
        requireFirebaseFunctions(),
        'gmailOAuthStart',
      );
      const result = await callable();
      window.location.assign(result.data.authorizationUrl);
    } catch {
      setError('Non è stato possibile avviare il collegamento Google. Riprova.');
      setAction(null);
    }
  };

  const reactivateWatch = async () => {
    setAction('watch');
    setError(null);
    try {
      const callable = httpsCallable<void, unknown>(
        requireFirebaseFunctions(),
        'initializeReportMailboxWatch',
      );
      await callable();
      await loadStatus();
    } catch {
      setError('Non è stato possibile riattivare la ricezione automatica.');
      setAction(null);
    }
  };

  const connected = status?.connected === true;
  const watchActive = status?.watch.active === true;
  const connectedSources = sources
    ? Object.values(sources.connections).filter(({ connected: sourceConnected }) => sourceConnected).length
    : 0;
  const sourcesDirty = sources
    ? Object.entries(sourceForm(sources)).some(([key, value]) => sourceDraft[key as keyof DriveSourceForm].trim() !== value)
    : false;

  const refreshAll = async () => {
    await Promise.all([loadSources(), loadStatus()]);
  };

  return (
    <section className="settings-tab-panel settings-workspace-panel" aria-labelledby="workspace-settings-title">
      <header className="page-heading settings-panel-heading">
        <div>
          <p className="app-eyebrow">Configurazione dati</p>
          <h2 id="workspace-settings-title">Sorgenti Drive</h2>
          <p>Indica una volta dove recuperare e archiviare i file: Talos userà automaticamente questi collegamenti.</p>
        </div>
        <button className="button button-secondary" type="button" disabled={action !== null || sourceLoading || sourceSaving} onClick={() => void refreshAll()}>
          {action === 'refresh' || sourceLoading ? <LoaderCircle className="spin" size={16} /> : <RefreshCw size={16} />}
          Verifica collegamenti
        </button>
      </header>

      <section className="settings-integration-card settings-drive-card" aria-labelledby="drive-sources-title">
        <div className="settings-card-heading">
          <span className="settings-service-icon"><HardDrive size={24} /></span>
          <div>
            <p>Google Drive</p>
            <h2 id="drive-sources-title">Collegamenti operativi</h2>
            <span>Incolla i link completi. Al salvataggio verifichiamo accesso e tipo di ogni risorsa.</span>
          </div>
          <span className={`settings-status${connectedSources === 3 ? ' active' : ''}`}>
            <span />
            {sourceLoading ? 'Verifica…' : `${connectedSources} di 3 collegati`}
          </span>
        </div>

        <form className="drive-source-form" onSubmit={(event) => { event.preventDefault(); void saveSources(); }}>
          <SourceLinkField
            id="workspace-folder-url"
            icon={<Database size={19} />}
            label="Archivio principale"
            description="Contiene Export/Export annuale e riceve i file caricati per la validazione."
            value={sourceDraft.workspaceFolderUrl}
            connection={sources?.connections.workspace}
            disabled={sourceLoading || sourceSaving || !canEditSources || Boolean(user?.demo)}
            onChange={(value) => updateSource('workspaceFolderUrl', value)}
          />
          <SourceLinkField
            id="mail-import-folder-url"
            icon={<Mail size={19} />}
            label="Report ricevuti via mail"
            description="Cartella di destinazione per gli allegati Excel, organizzati per anno e mese."
            value={sourceDraft.mailImportFolderUrl}
            connection={sources?.connections.mailImport}
            disabled={sourceLoading || sourceSaving || !canEditSources || Boolean(user?.demo)}
            onChange={(value) => updateSource('mailImportFolderUrl', value)}
          />
          <SourceLinkField
            id="responses-spreadsheet-url"
            icon={<FileSpreadsheet size={19} />}
            label="Foglio Risposte"
            description="Google Sheets usato nelle elaborazioni e nei report sulle ragioni di ingresso."
            value={sourceDraft.responsesSpreadsheetUrl}
            connection={sources?.connections.responses}
            disabled={sourceLoading || sourceSaving || !canEditSources || Boolean(user?.demo)}
            onChange={(value) => updateSource('responsesSpreadsheetUrl', value)}
          />

          {sourceError && <div className="settings-error drive-source-feedback" role="alert">{sourceError}</div>}
          {sourceSuccess && <div className="settings-success drive-source-feedback" role="status"><Check size={16} />{sourceSuccess}</div>}

          <footer className="drive-source-footer">
            <div>
              <ShieldCheck size={18} />
              <span>
                <strong>Permessi Drive</strong>
                Condividi le risorse con <code>report-crm-drive@reportcrm-of-justbit.iam.gserviceaccount.com</code>; per le cartelle assegna il ruolo Editor.
              </span>
            </div>
            <button className="button button-primary" type="submit" disabled={!canEditSources || Boolean(user?.demo) || sourceLoading || sourceSaving || !sourcesDirty}>
              {sourceSaving ? <LoaderCircle className="spin" size={16} /> : <Save size={16} />}
              {sourceSaving ? 'Verifica in corso…' : 'Salva collegamenti'}
            </button>
          </footer>
        </form>
      </section>

      <div className="settings-section-label">
        <Mail size={16} />
        <div><strong>Automazione Gmail</strong><span>Ricezione degli allegati nella cartella configurata sopra.</span></div>
      </div>

      <section className="settings-integration-card" aria-labelledby="gmail-integration-title">
        <div className="settings-card-heading">
          <span className="settings-service-icon"><Mail size={24} /></span>
          <div>
            <p>Google Workspace</p>
            <h2 id="gmail-integration-title">Ricezione report via Gmail</h2>
            <span>Evento immediato alla consegna, senza controlli periodici della casella.</span>
          </div>
          <span className={`settings-status${connected && watchActive ? ' active' : ''}`}>
            <span />
            {connected && watchActive ? 'Attiva' : connected ? 'Da riattivare' : 'Non collegata'}
          </span>
        </div>

        <div className="settings-flow" aria-label="Flusso automatico dei report">
          <div>
            <span><Mail size={19} /></span>
            <small>Casella collector</small>
            <strong>{status?.mailboxEmail ?? 'francesco.pace@justbit.it'}</strong>
          </div>
          <i aria-hidden="true" />
          <div>
            <span><ShieldCheck size={19} /></span>
            <small>Destinatario filtrato</small>
            <strong>{status?.recipientEmail ?? 'reportcrm-of@justbit.it'}</strong>
          </div>
          <i aria-hidden="true" />
          <div>
            <span><FolderOpen size={19} /></span>
            <small>Destinazione</small>
            <strong>Drive · Mail / anno / mese</strong>
          </div>
        </div>

        <div className="settings-details-grid">
          <div>
            <span>Autorizzazione Gmail</span>
            <strong>{connected ? 'Concessa in sola lettura' : 'Da completare'}</strong>
            <small>La dashboard non invia, modifica o elimina messaggi.</small>
          </div>
          <div>
            <span>Ricezione eventi</span>
            <strong>{watchActive ? 'Pub/Sub attivo' : 'Non attiva'}</strong>
            <small>{watchActive ? `Rinnovo entro ${formattedDate(status?.watch.expirationAt ?? null)}` : 'Si attiva dopo il collegamento Google.'}</small>
          </div>
          <div>
            <span>Ultima elaborazione</span>
            <strong>{formattedDate(status?.watch.lastProcessedAt ?? null)}</strong>
            <small>Vengono elaborati soltanto allegati Excel .xlsx riconosciuti.</small>
          </div>
        </div>

        {error && <div className="settings-error" role="alert">{error}</div>}

        <footer className="settings-card-footer">
          <div>
            <ShieldCheck size={18} />
            <span><strong>Accesso minimo</strong> Il token è cifrato e limitato alla lettura Gmail.</span>
          </div>
          <div className="settings-actions">
            <a className="button button-secondary" href={sourceDraft.mailImportFolderUrl} target="_blank" rel="noreferrer">
              Apri cartella Drive <ArrowUpRight size={15} />
            </a>
            {connected && !watchActive ? (
              <button className="button button-primary" type="button" disabled={action !== null} onClick={() => void reactivateWatch()}>
                {action === 'watch' ? <LoaderCircle className="spin" size={16} /> : <CloudUpload size={16} />}
                Riattiva ricezione
              </button>
            ) : (
              <button className="button button-primary" type="button" disabled={action !== null || user?.demo} onClick={() => void connectMailbox()}>
                {action === 'connect' ? <LoaderCircle className="spin" size={16} /> : connected ? <RefreshCw size={16} /> : <Check size={16} />}
                {connected ? 'Ricollega account' : 'Collega account Google'}
              </button>
            )}
          </div>
        </footer>
      </section>

      <section className="settings-rule-card">
        <span><FileSpreadsheet size={22} /></span>
        <div>
          <p>Regola di archiviazione</p>
          <h2>Un solo report per data di riferimento</h2>
          <p>Il nome dell’allegato determina il giorno. Se arriva una nuova versione per la stessa data, il file esistente viene aggiornato mantenendo un unico documento.</p>
        </div>
      </section>
    </section>
  );
}

function SourceLinkField({
  id,
  icon,
  label,
  description,
  value,
  connection,
  disabled,
  onChange,
}: {
  id: string;
  icon: React.ReactNode;
  label: string;
  description: string;
  value: string;
  connection?: DriveConnectionCheck;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="drive-source-field">
      <span className="drive-source-field-icon">{icon}</span>
      <label htmlFor={id}>
        <span className="drive-source-field-heading">
          <strong>{label}</strong>
          {connection && (
            <small className={connection.connected ? 'connected' : 'disconnected'}>
              <span />{connection.connected ? connection.name ?? 'Accessibile' : 'Non accessibile'}
            </small>
          )}
        </span>
        <span className="drive-source-description">{description}</span>
        <span className="drive-source-input-row">
          <Link2 size={16} />
          <input
            id={id}
            type="url"
            inputMode="url"
            autoComplete="off"
            placeholder="https://drive.google.com/…"
            value={value}
            disabled={disabled}
            required
            onChange={(event) => onChange(event.target.value)}
          />
          {value && (
            <a href={value} target="_blank" rel="noreferrer" aria-label={`Apri ${label} in Google Drive`} title="Apri in Google Drive">
              <ArrowUpRight size={17} />
            </a>
          )}
        </span>
      </label>
    </div>
  );
}

type SettingsTab = 'workspace' | 'aree-territoriali' | 'gestione-code-province' | 'utenti-accessi';

export function SettingsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const isSuperadmin = user?.role === 'superadmin';
  const isAdmin = user?.role === 'admin';

  if (!user || user.role === 'utente') {
    return <Navigate to="/app" replace />;
  }

  const adminRequestedTab = requestedTab === 'aree-territoriali'
    || requestedTab === 'gestione-code-province'
    || requestedTab === 'regole-territoriali';
  if (isAdmin && !adminRequestedTab) {
    return <Navigate to="/app/impostazioni?tab=aree-territoriali" replace />;
  }

  const activeTab: SettingsTab = requestedTab === 'aree-territoriali'
    ? 'aree-territoriali'
    : requestedTab === 'gestione-code-province' || requestedTab === 'regole-territoriali'
      ? 'gestione-code-province'
      : requestedTab === 'utenti-accessi' && isSuperadmin
        ? 'utenti-accessi'
        : 'workspace';

  const tabs = [
    ...(isSuperadmin
      ? [{ id: 'workspace' as const, label: 'Sorgenti Drive', icon: HardDrive, to: '/app/impostazioni' }]
      : []),
    { id: 'aree-territoriali' as const, label: 'Aree territoriali', icon: Map, to: '/app/impostazioni?tab=aree-territoriali' },
    { id: 'gestione-code-province' as const, label: 'Gestione code e province', icon: MapPinned, to: '/app/impostazioni?tab=gestione-code-province' },
    ...(isSuperadmin
      ? [{ id: 'utenti-accessi' as const, label: 'Utenti e accessi', icon: UserRoundCog, to: '/app/impostazioni?tab=utenti-accessi' }]
      : []),
  ];

  return (
    <main className="dashboard-page settings-page settings-hub-page">
      <header className="page-heading settings-heading">
        <div>
          <p className="app-eyebrow">Amministrazione</p>
          <h1>Impostazioni</h1>
          <p>{isSuperadmin
            ? 'Gestisci sorgenti dati, struttura territoriale, code e accessi da un unico punto.'
            : 'Gestisci la struttura territoriale e l’assegnazione delle code per provincia.'}</p>
        </div>
      </header>

      <nav className="settings-tabs" aria-label="Sezioni impostazioni">
        {tabs.map(({ id, label, icon: Icon, to }) => (
          <Link
            key={id}
            className={activeTab === id ? 'active' : undefined}
            to={to}
            aria-current={activeTab === id ? 'page' : undefined}
          >
            <Icon size={17} />
            <span>{label}</span>
          </Link>
        ))}
      </nav>

      {activeTab === 'workspace' && isSuperadmin && <WorkspaceSettingsPanel />}
      {activeTab === 'aree-territoriali' && <TerritorialAreasPage />}
      {activeTab === 'gestione-code-province' && <ProvinceQueuesPage />}
      {activeTab === 'utenti-accessi' && isSuperadmin && <UsersPage embedded />}
    </main>
  );
}
